/**
 * telephonyCampaignLoginApi
 *
 * BB-6-login-work-order Stage-6 completion: client for the real,
 * authenticated Workspace Campaign Login API
 * (`GET /api/telephony/bb6/campaigns`, `POST /api/telephony/bb6/login`;
 * backend/app/routers/telephony_bb6_campaign_login.py).
 *
 * Wraps the SAME shared `api` axios instance every other real-backed
 * surface already uses (see lib/api.js, emailDeliveryDashboardApi.js)
 * -- no second HTTP stack. Auth (Bearer token) and account scoping
 * (X-Account-ID header) are attached automatically by that instance's
 * own request interceptor; this module never accepts or forwards a
 * caller-supplied account id, VICIdial username, or password -- there
 * is no parameter on either function through which one could be
 * supplied. The backend derives the agent's own VICIdial identity
 * entirely server-side.
 *
 * `getAuthorizedCampaigns()` returns `{ campaigns: [{campaign_id,
 * campaign_name, available_leads}] }` -- `available_leads` is the
 * truthful, user-facing "Available Leads" figure (the approved P2
 * product-level sum of VICIdial's own native
 * vicidial_campaign_stats.dialable_leads and the actionable-hopper
 * count; see backend/app/telephony/provider_query_adapter/operations.py
 * for the full evidence/rationale), never hopper READY depth (that
 * value is never returned by this API at all). A backend
 * 409 (`not_provisioned`) or 503 (`login_unavailable`) is translated
 * into a typed `{status, campaigns: []}` result rather than thrown, so
 * the calling component can render an explanatory empty state without
 * its own try/catch for these two expected, non-exceptional backend
 * states. Any other backend error (502 `provider_unavailable`, network
 * failure) is re-thrown -- `WorkspacePreviewGate` catches it and shows
 * a generic "temporarily unavailable" state, never the raw error.
 *
 * `loginToCampaign(campaignId)` posts `{campaign_id: campaignId}` --
 * the only field this API ever accepts -- and returns the backend's
 * own normalized `{state, failure_reason}` body unchanged. Callers
 * must never treat a resolved promise alone as login success: `state`
 * is `"logged_in_paused"` only when the backend's own SESSION_LOGIN
 * executor (PR #307) has already completed its fresh, observation-
 * gated confirmation -- this function performs no optimistic
 * interpretation of its own.
 */
import { api } from "./api";

export const CAMPAIGN_LOGIN_STATUS = Object.freeze({
  OK: "ok",
  NOT_PROVISIONED: "not_provisioned",
  UNAVAILABLE: "unavailable",
});

export async function getAuthorizedCampaigns() {
  try {
    const res = await api.get("/telephony/bb6/campaigns");
    return { status: CAMPAIGN_LOGIN_STATUS.OK, campaigns: res.data.campaigns };
  } catch (error) {
    const status = error?.response?.status;
    if (status === 409) {
      return { status: CAMPAIGN_LOGIN_STATUS.NOT_PROVISIONED, campaigns: [] };
    }
    if (status === 404 || status === 503) {
      return { status: CAMPAIGN_LOGIN_STATUS.UNAVAILABLE, campaigns: [] };
    }
    throw error;
  }
}

export async function loginToCampaign(campaignId) {
  const res = await api.post("/telephony/bb6/login", { campaign_id: campaignId });
  return res.data;
}

/**
 * BB-8 work order Stage 1-6: `POST /api/telephony/bb6/start-dialing` /
 * `POST /api/telephony/bb6/pause`
 * (backend/app/routers/telephony_bb6_campaign_login.py). Both accept
 * only `{campaign_id}` -- no VICIdial session material, agent_log_id,
 * or server_ip field exists on either request, and neither is ever
 * returned. Both return the backend's own normalized
 * `{state, failure_reason}` body unchanged -- `state` is `"active"`/
 * `"paused"` only when the backend's own PRESENCE executor has already
 * completed its fresh, observation-gated confirmation (mirrors
 * `loginToCampaign`'s own "never treat a resolved promise alone as
 * success" discipline exactly).
 *
 * A 404 here means `TELEPHONY_BB6_PRESENCE_ENABLED` is off (Stage 7 is
 * not yet authorized in any environment as of this Stage-1-6 slice) --
 * callers must treat this the same as any other "temporarily
 * unavailable" case, never as a schema/auth error.
 */
export async function startDialing(campaignId) {
  const res = await api.post("/telephony/bb6/start-dialing", { campaign_id: campaignId });
  return res.data;
}

export async function pausePresence(campaignId) {
  const res = await api.post("/telephony/bb6/pause", { campaign_id: campaignId });
  return res.data;
}

/**
 * BB-6L Campaign Logout: `POST /api/telephony/bb6/logout`.
 *
 * **Campaign Logout is not App Logout.** This ends the native VICIdial
 * campaign session and releases MissionAssist telephony ownership;
 * MissionAssist authentication is untouched and the user stays signed
 * in. Nothing here clears an auth token.
 *
 * Takes only `{campaign_id}`, exactly like `loginToCampaign` and the
 * Presence commands -- the agent, account and provider connection all
 * come from the authenticated context, so no browser can log out
 * someone else's session.
 *
 * Returns the backend's bounded `{outcome, logged_out, ...}` body
 * unchanged. Treat `logged_out === true` as the only success: a refusal
 * (`owned_call_connected`, `wrap_up_pending`, ...) is a 200 with
 * `logged_out === false`, because logging out during an owned call
 * would silently hang up a live customer.
 *
 * A 404 means `TELEPHONY_BB6_CAMPAIGN_LOGOUT_ENABLED` is off -- treat it
 * as temporarily unavailable, never as a schema or auth error.
 */
export async function logoutCampaign(campaignId) {
  const res = await api.post("/telephony/bb6/logout", { campaign_id: campaignId });
  return res.data;
}

/**
 * BB-11 call lifecycle: `GET /api/telephony/call/state` /
 * `POST /api/telephony/call/hangup`
 * (backend/app/routers/telephony_bb11_call_lifecycle.py).
 *
 * **Neither function takes a parameter, and that is the contract.**
 * Both backend routes are body-less by design: the browser has no
 * field in which to name a call, because BB-10's release UPDATE
 * carries no callerid predicate -- a client able to name a channel
 * could end a call it does not own. The backend derives the agent
 * session from the authenticated principal and supplies every call
 * identifier from the claim it made itself.
 *
 * Both return `{phase, refusal, lead_retained}` and nothing else. No
 * callerid, channel, uniqueid, call_server_ip, lead_id, called_count
 * or customer telephone number exists anywhere in either response, so
 * none can reach client state.
 *
 * `phase` is `"wrap_up"` only when the backend has already verified
 * the native release against fresh provider state. Callers must never
 * treat a resolved promise as release -- the same discipline
 * `loginToCampaign` and `startDialing` already follow.
 *
 * A 404 means `TELEPHONY_BB11_CALL_LIFECYCLE_ENABLED` is off; callers
 * treat it as "temporarily unavailable", never as a schema or auth
 * error.
 */
export const CALL_PHASE = Object.freeze({
  IDLE: "idle",
  ACQUIRING: "acquiring",
  CONNECTED: "connected",
  HANGING_UP: "hanging_up",
  WRAP_UP: "wrap_up",
  // BB-14g. Product Active was entered, the acquisition obligation was
  // established, and it then failed in a way the backend could not
  // re-establish within its bounded budget. Distinct from ENDED, which
  // is an unremarkable exit: this session still believes it is Active
  // while its only path to a call is gone. Native may still be QUEUE,
  // so it is emphatically not Paused.
  ACQUISITION_FAILED: "acquisition_failed",
  ENDED: "ended",
  // AW-2B3. The owned call ended, the provider session is gone, and the
  // native lead this call claimed is still unresolved -- so there is no
  // agent session left through which it could be dispositioned.
  //
  // Deliberately NOT `ENDED`. `ENDED` is a settled, unremarkable exit
  // that leaves the session dialing; this one asserts the opposite on
  // both halves -- the session is over, and something is still owed on
  // a real customer's record. Deliberately NOT `WRAP_UP` either, which
  // would claim a disposition is reachable when the backend has proven
  // it is not.
  //
  // Recognised here ahead of the backend that will emit it. Nothing in
  // Production produces this value yet, so every branch keyed on it is
  // unreachable until that lands -- which is the point: a phase the
  // shell does not know falls through to the Active default, and
  // `acquisition_failed` already proved what that costs.
  RETAINED_LEAD_STRANDED: "retained_lead_stranded",
});

export async function getCallState() {
  const res = await api.get("/telephony/call/state");
  return res.data;
}

export async function hangupCall() {
  const res = await api.post("/telephony/call/hangup");
  return res.data;
}

/**
 * BB-12 -- the dispositions this agent may choose right now.
 *
 * Decided server-side: provider-selectable intersected with the set
 * BB-12 can actually execute. The browser renders what it is given and
 * never widens it -- a choice the backend did not offer is a choice the
 * backend will refuse.
 *
 * Empty outside Wrap-up, so a disposition control cannot be rendered
 * for a call that does not exist.
 */
export async function getDispositionOptions() {
  try {
    const res = await api.get("/telephony/call/disposition/options");
    return res?.data?.options ?? {};
  } catch (error) {
    return {};
  }
}

/**
 * BB-12 -- disposition the retained call.
 *
 * **Sends one field.** Lead, list, agent log, uniqueid, callerid,
 * session name and credentials are all resolved server-side from state
 * MissionAssist already verified; a browser that could name a lead
 * could disposition someone else's call.
 *
 * `completed` is true only after the backend read fresh provider state
 * and found the agent paused, the lead released and the lead's own
 * status equal to the choice. The UI must never leave Wrap-up on
 * anything weaker.
 */
export async function disposeCall(disposition, { pauseAfter = false } = {}) {
  const res = await api.post("/telephony/call/disposition", {
    disposition,
    // BB-12C. One-shot "Pause after disposition". It can only suppress
    // the automatic return to dialing -- never grant it.
    pause_after: pauseAfter,
  });
  return res.data;
}

/**
 * P2-R1 Workspace session restoration: `GET /api/telephony/bb6/session`.
 *
 * **Read-only observation, never adoption.** A page mounting must not be
 * able to change native presence, so this issues no command and takes
 * no parameter -- the backend answers only from an S1 lease the
 * authenticated agent already owns, inside their own Account and
 * provider connection.
 *
 * Returns `{session, campaign_id, campaign_name, lifecycle_state}`.
 * `session: false` is the ordinary answer for an agent with no live
 * campaign session, and means the Campaign Login gate should render
 * exactly as it always has. `lifecycle_state` is MissionAssist's own
 * runtime vocabulary (`logged_in` / `active`), not native presence.
 */
export async function getWorkspaceSession() {
  const res = await api.get("/telephony/bb6/session");
  return res.data;
}

/**
 * BB-14e native presence reconciliation:
 * `GET /api/telephony/bb6/presence`.
 *
 * **Observation, not a command.** It moves nothing; it reports what
 * native says this agent is doing. It exists because after Start Dialing
 * the Workspace otherwise learns nothing except BB-11 call state, so a
 * native-initiated pause -- an AMD/DROP auto-pause, a supervisor pause,
 * a native timeout -- was invisible and left the agent showing Active
 * indefinitely.
 *
 * Returns one bounded field. `"active"` covers native READY and QUEUE;
 * QUEUE is the pre-claim window and is deliberately never a product
 * state of its own. `"paused"` means native paused with no claimed call.
 *
 * **`"unknown"` must be ignored, never rendered.** It is the fail-closed
 * answer for a provider fault, a missing row, an unrecognised status, a
 * campaign mismatch, and for `INCALL` -- where BB-9/BB-9c/BB-11 remain
 * the only authority for Connected and Wrap-up. A 404 (flag off) is
 * normalised to `"unknown"` for the same reason.
 */
export const NATIVE_PRESENCE = Object.freeze({
  ACTIVE: "active",
  PAUSED: "paused",
  UNKNOWN: "unknown",
});

export async function observeNativePresence(campaignId) {
  try {
    const res = await api.get("/telephony/bb6/presence", {
      params: campaignId ? { campaign_id: campaignId } : undefined,
    });
    const state = res?.data?.state;
    return state === NATIVE_PRESENCE.ACTIVE || state === NATIVE_PRESENCE.PAUSED
      ? state
      : NATIVE_PRESENCE.UNKNOWN;
  } catch (error) {
    // Never throws into the poll loop. Any failure is "we cannot say".
    return NATIVE_PRESENCE.UNKNOWN;
  }
}

/**
 * AW-2B2 -- the effective Script for the agent's current owned call.
 *
 * Non-mutating. Returns `{resolved, outcome, script_id}`, where
 * `outcome` is the server's own vocabulary: `resolved`,
 * `no_script_assigned`, `script_not_found`, `no_published_version`,
 * `resolution_unavailable`, `not_provisioned`.
 *
 * The request carries nothing at all -- no Account, provider
 * connection, Campaign, List, Lead, Script or version. Every one of
 * those is derived server-side from the authenticated session, the
 * active S1 lease and the durable acquisition obligation. There is no
 * parameter here through which a browser could state any of them.
 */
export async function getEffectiveScript() {
  const res = await api.get("/telephony/script/effective");
  return res.data;
}

/**
 * AW-2B3.1 -- start (or recover) the Script run for the owned call.
 *
 * Returns `{run_id, script_id}`, and those two identities are the ONLY
 * ones the Production workspace may run a Script from.
 *
 * **Deliberately sends no body.** The endpoint accepts an optional
 * `client_run_id`, but AW-2B3.1 made run identity server-owned: the run
 * is bound to the authoritative owned call (Account, provider
 * connection, agent, S1 generation and the native call fence), so a
 * refresh, a remount, a retry, a second tab and two concurrent requests
 * all converge on the same run. A browser-minted token can only be
 * staler than that, and this client must not invent call identity it
 * does not own -- so it does not send one.
 */
export async function startEffectiveScriptRun() {
  const res = await api.post("/telephony/script/run/start", {});
  return res.data;
}
